/**
 * useWizardDraftStore — den ene state-eier for next-wizard (parent feature WI 1082).
 *
 * Scope: kun next-wizardene (CreateFromFile + ImportToExisting via WizardShell).
 * Legacy-storene `tableCreationWizardStore` / `tableWizardStore` brukes
 * fortsatt av de gamle TableSetup-skjermene og skal ikke røres herfra. Se
 * `docs/frontend/wizard-next-store-migration.md` for kartleggingen.
 *
 * Server-sync følger import-session-mønsteret:
 *   1. `initDraft(mode)` setter mode lokalt; ingen BE-roundtrip enda.
 *   2. Første artefakt-setter trigger `ensureDraft()` som POSTer for å få
 *      tildelt `publicId`/`draftId` på BE.
 *   3. Hver setter (`setTable`, `setFileConfig`, ...) er en async
 *      action som oppdaterer lokal state optimistisk, kaller riktig per-
 *      artefakt PUT, og hydrerer status + nextStep fra responsen.
 *   4. Coalescing per setter sikrer at to in-flight PUTs på samme artefakt
 *      ikke kolliderer — andre kall venter på utestående promise og kjører
 *      etter at den er resolved (last-save-wins-mønsteret).
 */

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  WizardColumnMappingDraft,
  WizardColumnMappingPair,
  WizardDraftState,
  WizardDraftStatus,
  WizardDraftTable,
  WizardDuplicateResolution,
  WizardFileConfigDraft,
  WizardImportDefinitionDraft,
  WizardMode,
  WizardSyncStatus,
  WizardValidationRuleDraft,
} from '@/types/wizardDraft';
import { asDatasetId, type ContainerId, type DatasetId } from '@/types/brandedIds';
import {
  createDraft,
  deleteDraft,
  executeDraft,
  fetchDraft,
  putColumnMapping,
  putImportDefinition,
  putTable,
  putUpload,
  putValidation,
  type WizardDraftResponse,
  type WizardExecuteResult,
} from '@/api/endpoints/wizardDraft';
import {
  getValidationBindingsByTable,
  updateValidationRule,
  type ValidationRuleResponse,
  type ValidationSeverity,
} from '@/api/endpoints/validationRules';
import type { AnalyseCol } from '@/store/importWizardStore';
import { useFilePreviewStore } from '@/store/Setup/filePreviewStore';
import { useNotificationsStore } from '@/store/notificationsStore';
import { humanizeNetworkError } from '@/helpers/errorHandlingHelpers';

function initialState (): WizardDraftState {
  return {
    draftId: null,
    publicId: null,
    mode: null,
    currentStep: 1,
    currentStepSlug: '',
    table: null,
    fileConfig: null,
    importDefinition: null,
    columnMapping: null,
    validationRules: [],
    status: 'draft',
    nextStep: null,
    fileColumns: null,
    setupFileAnalysis: null,
    validationRuleWarnings: null,
    stepGates: null,
    syncStatus: 'idle',
    lastSyncedAt: null,
  };
}

function describeSyncError (err: unknown): string {
  // Bug 2841: manglende serverkontakt → humanisert melding før rå transport-tekst («Network Error»).
  const networkMessage = humanizeNetworkError(err);
  if (networkMessage) return networkMessage;
  if (err instanceof Error && err.message.length > 0) return err.message;
  if (typeof err === 'string' && err.length > 0) return err;
  return 'Ukjent feil ved synk mot server';
}

function normalizeStatus (raw: unknown): WizardDraftStatus {
  const value = typeof raw === 'string' ? raw.toLowerCase() : '';
  if (value === 'complete') return 'complete';
  if (value === 'failed') return 'failed';
  return 'draft';
}

export interface AutoMatchOptions {
  /** Tabell-nøkkel for analysen (publicId for tabellen). Hvis tom — toast + abort. */
  tableKey: string;
  /** Eksisterende mal å bruke som hint (null for «ny / manuell»-modus). */
  importDefinitionKey: string | null;
}

/**
 * Build the auto-match column mapping from analysed entries.
 *
 * Merges each verified or suggested source column into the current pairs list
 * updating an existing pair in place or appending a new one and tallies a
 * per-status count plus the number of applied matches. Object identity of the
 * incoming pairs is preserved (shallow slice) so callers observe the same in
 * place updates as before this extraction.
 */
function buildAutoMatchMapping (
  entries: AnalyseCol[],
  currentPairs: WizardColumnMappingPair[],
): { next: WizardColumnMappingPair[]; statusCounts: Record<string, number>; appliedCount: number } {
  const next: WizardColumnMappingPair[] = currentPairs.slice();
  const statusCounts: Record<string, number> = {};
  let appliedCount = 0;
  for (const entry of entries) {
    const entryStatus = entry.mappingStatus ?? '(ukjent)';
    statusCounts[entryStatus] = (statusCounts[entryStatus] ?? 0) + 1;
    if (!entry.sourceColumnName || !entry.tableColumnName) continue;
    if (entryStatus !== 'verified map' && entryStatus !== 'suggestion') continue;
    const existing = next.find((p) => p.sourceColumnName === entry.sourceColumnName);
    if (existing) {
      existing.targetColumnKey = entry.tableColumnName;
    } else {
      next.push({
        sourceColumnName: entry.sourceColumnName,
        targetColumnKey: entry.tableColumnName,
        transform: null,
        isKey: Boolean(entry.possiblyKey),
      });
    }
    appliedCount++;
  }
  return { next, statusCounts, appliedCount };
}

function isHttpNotFound (err: unknown): boolean {
  const axiosLike = err as { response?: { status?: number } } | null;
  return axiosLike?.response?.status === 404;
}

export const useWizardDraftStore = defineStore('wizardDraft', () => {
  const state = ref<WizardDraftState>(initialState());
  /** Busy-flag for auto-match — settes av storen mens analyse + PUT pågår. */
  const autoMatchBusy = ref<boolean>(false);
  // WI 1965: to signaler med ulik semantikk. BE sletter ikke lenger draften ved
  // execute — en feilet/avbrutt kjøring rettes og kjøres på nytt i samme wizard.
  // - hasStartedRun: en kjøring er køet (execute-enqueue) — grunnlag for
  //   resultat-steg-resume (bug 1971), uavhengig av utfall.
  // - hasCompletedSuccessfully: kjøringen nådde terminal SUKSESS (markert av
  //   import-progress-steget) — grunnlag for «Starte en ny import?»-ordlyden
  //   (bug 1947) og klient-eid draft-cleanup. Feilet kjøring setter den ALDRI.
  const startedRunRef = ref<boolean>(false);
  const runSucceededRef = ref<boolean>(false);
  const hasStartedRun = computed<boolean>(() => startedRunRef.value);
  const hasCompletedSuccessfully = computed<boolean>(() => runSucceededRef.value);
  // Bug #1971: execute-resultatet (uploadId/tableKey) eies av storen — view-lokale
  // bridge-refs dør ved remount (history-back fra feillisten/rapporten), og BE har
  // ryddet draften, så dette er eneste kilde for å gjenoppta resultat-steget intakt.
  const lastExecuteResultRef = ref<WizardExecuteResult | null>(null);
  const lastExecuteResult = computed<WizardExecuteResult | null>(() => lastExecuteResultRef.value);
  // Bug #2067: tabellens etablerte valideringsregler for valgt måltabell.
  // Eies av storen (ikke view-lokale refs) så både steg 5 og sammendragspanelet
  // leser samme sannhet: steg 5 viser dem kildemerket («Fra tabellen»), og
  // sammendraget teller tabellens aktive + draftens nye. Ligger UTENFOR
  // WizardDraftState — de er tabellens (BE-eide), ikke en del av draften.
  const establishedTableRulesRef = ref<ValidationRuleResponse[]>([]);
  const establishedTableRules = computed<ValidationRuleResponse[]>(() => establishedTableRulesRef.value);
  // US 1848 task 2058: feil-eierskap for save-or-run-steget flyttet fra .vue til
  // storen. Banneret på steget («Lagre mal og avslutt» / «Lagre mal og kjør
  // import») binder mot denne reaktive feil-staten — storen eier feilteksten,
  // komponenten bare leser den (Pattern B / reaktiv error-ref).
  const saveOrRunErrorRef = ref<string | null>(null);
  const saveOrRunError = computed<string | null>(() => saveOrRunErrorRef.value);
  // Task 2583 (US 2524): duplikat-valget for DENNE kjøringen + siste prøve-
  // kjørings duplikat-teller. REN in-memory state (bruker-avklaring 2026-07-09):
  // aldri via draft-setterne / Redis-draften — valget sendes kun i execute-body
  // og persisteres aldri på draft/importoppsett/prosjekt. Telleren eies av
  // storen så «Lagre eller kjør»-steget kan gate kjøring på tvers av steg
  // (prøvekjøringsstegets lokale result-ref dør ved stegbytte-remount).
  const duplicateResolutionRef = ref<WizardDuplicateResolution | null>(null);
  const duplicateResolution = computed<WizardDuplicateResolution | null>(() => duplicateResolutionRef.value);
  const dryRunDuplicatesRef = ref<number>(0);
  const dryRunDuplicates = computed<number>(() => dryRunDuplicatesRef.value);

  const draftId = computed(() => state.value.draftId);
  const publicId = computed(() => state.value.publicId);
  const mode = computed(() => state.value.mode);
  const currentStep = computed(() => state.value.currentStep);
  const currentStepSlug = computed(() => state.value.currentStepSlug);
  const table = computed(() => state.value.table);
  const fileConfig = computed(() => state.value.fileConfig);
  const importDefinition = computed(() => state.value.importDefinition);
  const columnMapping = computed(() => state.value.columnMapping);
  const validationRules = computed(() => state.value.validationRules);
  /**
   * WI 2317/2318: den ENE FE-lesbare referansen til datasettet en import faktisk
   * kjører mot — branded `DatasetId`. BE-eid og resolved server-side
   * (WI 1619 + 2314/2315): for en fler-arks arbeidsbok er `dataUploadId`
   * child-uploaden BE klassifiserte som data; for en én-til-én fil/CSV er den
   * ikke satt, og container-blobben ER datasettet — vi caster `uploadId`
   * (ContainerId) eksplisitt til DatasetId via cast-stien (semantisk: «for en
   * én-arks arbeidsbok er containeren datasettet»). Ren `computed` — FE LESER
   * kun, setter aldri `dataUploadId`/`resolvedDatasetId` (de utelates i
   * FE-byggede PUT-payloads). Erstatter den gamle FE-synkede
   * `uploadStore.selectedDataset`/`getSelectedDatasetUploadId`-kjeden.
   */
  const resolvedDatasetId = computed<DatasetId | null>(() => {
    const config = state.value.fileConfig;
    if (!config) return null;
    return config.dataUploadId ?? asDatasetId(config.uploadId);
  });
  /**
   * WI 2318: containeren (ContainerId) — den ENE referansen FE selv holder.
   * Read-only speil av draftens `fileConfig.uploadId`.
   */
  const containerId = computed<ContainerId | null>(() => state.value.fileConfig?.uploadId ?? null);
  const status = computed(() => state.value.status);
  const nextStep = computed(() => state.value.nextStep);
  const fileColumns = computed(() => state.value.fileColumns);
  const stepGates = computed(() => state.value.stepGates);

  /**
   * WI #1687: BE-eid blockReason for et gitt steg. Returnerer årsaken BE oppgir
   * når steget er ufullstendig, ellers null. Ren passthrough — FE bestemmer ALDRI
   * HVORFOR et steg er blokkert (det eier BE); FE bare viser teksten.
   */
  function blockReasonForStep (slug: string): string | null {
    const gate = state.value.stepGates?.find((g) => g.step === slug);
    if (!gate || !gate.incomplete) return null;
    const reason = gate.reason?.trim() ?? '';
    return reason.length > 0 ? reason : null;
  }
  const setupFileAnalysis = computed(() => state.value.setupFileAnalysis);
  const validationRuleWarnings = computed(() => state.value.validationRuleWarnings);
  const syncStatus = computed(() => state.value.syncStatus);
  const lastSyncedAt = computed(() => state.value.lastSyncedAt);

  /** True når BE har bekreftet at draften er komplett — UI-gate for dry-run/execute. */
  const isComplete = computed(() => state.value.status === 'complete');

  /** Callback fyrt av storen etter første vellykkede create. View-shells lytter
   *  for å oppdatere URL til `/import/<publicId>`. */
  type AfterCreateListener = (publicId: string) => void;
  const afterCreateListeners = new Set<AfterCreateListener>();
  function onAfterFirstSave (listener: AfterCreateListener): () => void {
    afterCreateListeners.add(listener);
    return () => { afterCreateListeners.delete(listener); };
  }
  function notifyAfterCreate (createdPublicId: string): void {
    for (const listener of afterCreateListeners) {
      try { listener(createdPublicId); } catch { /* ignore listener errors */ }
    }
  }

  function initDraft (newMode: WizardMode): void {
    // Sørger for cross-wizard isolation (bug G, smoketest 2026-05-20): hvis
    // bruker bytter fra wizard A til B uten å navigere via reset/cancel,
    // skal B starte med blank state. initialState() blanker publicId/draftId
    // og alle artefakter; vi behodler ingen rester av forrige draft.
    state.value = { ...initialState(), mode: newMode };
    lastSetUploadId = null;
    mappingOwnedByTemplate = false;
    establishedTableRulesRef.value = [];
    clearDuplicateState();
    // Bug #3013: run-tilstanden er per kjøring/draft — en ny veiviser-kjøring
    // skal aldri arve forrige kjørings «fullført»-signaler eller execute-resultat.
    clearRunState();
  }

  /** Bug #3013: nullstill per-kjøring-tilstanden (fullført-signaler + execute-resultat). */
  function clearRunState (): void {
    startedRunRef.value = false;
    runSucceededRef.value = false;
    lastExecuteResultRef.value = null;
  }

  /**
   * Smoketest 2.0 (#4 / #5 / #6 / #7): publicId må alltid være BE-eid og
   * globalt unik. Når URL mangler publicId-segment skal viewen kalle denne i
   * stedet for `initDraft` — den blanker lokal state OG roundtripper til BE
   * for å allokere publicId opp-front.
   *
   * Konsekvens:
   *  - Cross-wizard switch får ny BE-eid publicId; ingen state-leakage.
   *  - "Nullstill" treffer alltid eksisterende draft (ingen 404 fra
   *    cancel-på-aldri-opprettet-draft).
   *  - URL kan oppdateres umiddelbart via `onAfterFirstSave`-callbacken
   *    (samme mekanisme som første-setter-create).
   */
  async function initDraftFromBackend (newMode: WizardMode): Promise<string> {
    initDraft(newMode);
    return ensureDraft();
  }

  function setStep (step: number, slug: string): void {
    state.value.currentStep = step;
    state.value.currentStepSlug = slug;
  }

  function setSyncStatus (next: WizardSyncStatus, syncedAt?: string): void {
    state.value.syncStatus = next;
    if (syncedAt !== undefined) {
      state.value.lastSyncedAt = syncedAt;
    }
  }

  function resetDraft (): void {
    state.value = initialState();
    lastSetUploadId = null;
    mappingOwnedByTemplate = false;
    clearRunState();
    establishedTableRulesRef.value = [];
    clearDuplicateState();
  }

  // ---- Task 2583 (US 2524): duplikat-valg for kjøringen ------------------

  /** Sett brukerens duplikat-valg for denne kjøringen (kun i minnet). */
  function setDuplicateResolution (value: WizardDuplicateResolution | null): void {
    duplicateResolutionRef.value = value;
  }

  /**
   * Rapportér duplikat-telleren fra en fersk prøvekjøring. En NY prøvekjøring
   * betyr nytt beslutningsgrunnlag — et tidligere valg nullstilles alltid
   * (ingen forhåndsvalgt verdi; valget gjelder kun kjøringen det ble tatt for).
   */
  function setDryRunDuplicates (count: number): void {
    dryRunDuplicatesRef.value = count;
    duplicateResolutionRef.value = null;
  }

  function clearDuplicateState (): void {
    duplicateResolutionRef.value = null;
    dryRunDuplicatesRef.value = 0;
  }

  /** Bug #2067: sett tabellens etablerte regler direkte (testbart / synkront). */
  function setEstablishedTableRules (rules: ValidationRuleResponse[]): void {
    establishedTableRulesRef.value = [...rules];
  }

  /**
   * Bug #2065/#2067: hent tabellens etablerte valideringsregler for valgt
   * måltabell (tableKey/publicId). Tom/null nøkkel blanker listen. Feil
   * surfaces som notification — steg 5 skal ikke feile hardt på visningsdata.
   */
  async function refreshEstablishedTableRules (tableKey: string | null | undefined): Promise<void> {
    const key = String(tableKey ?? '').trim();
    if (!key) {
      establishedTableRulesRef.value = [];
      return;
    }
    try {
      establishedTableRulesRef.value = await getValidationBindingsByTable(key);
    } catch (err) {
      establishedTableRulesRef.value = [];
      surfaceSyncError(err, 'Kunne ikke hente tabellens valideringsregler');
    }
  }

  /**
   * Bug #2067: flip Advarsel/Feil på en av TABELLENS regler fra steg 5.
   * Det er tabellens regel som endres (PUT på regelen via eksisterende
   * update-endepunkt) — ingen import-lokal override. Lokal liste oppdateres
   * fra responsen så steg 5 og sammendraget reflekterer endringen umiddelbart;
   * prøvekjøringen leser regelgrunnlaget ferskt ved neste kjøring.
   */
  async function flipEstablishedRuleSeverity (
    rule: ValidationRuleResponse,
    severity: ValidationSeverity,
  ): Promise<void> {
    try {
      const updated = await updateValidationRule(rule.validationRuleId, {
        columnName: rule.columnName,
        parameters: rule.parameters,
        severity,
        isActive: rule.isActive,
      });
      establishedTableRulesRef.value = establishedTableRulesRef.value.map(
        (r) => (r.validationRuleId === rule.validationRuleId ? { ...r, severity: updated.severity } : r),
      );
    } catch (err) {
      surfaceSyncError(err, 'Kunne ikke endre regelens alvorlighetsgrad');
    }
  }

  function hydrateFromResponse (resp: WizardDraftResponse): void {
    // BE eier kontrakten — FE leser den verbatim, ingen ??-fallback-kjeder
    // (memory feedback_fe_consumes_be_contracts_verbatim). Hvis BE ikke
    // returnerer påkrevde identifikatorer, KAST eksplisitt feil heller enn å
    // hydrere stille med null. UI-en fanger feilen og viser notification.
    if (resp === null || typeof resp !== 'object') {
      throw new Error('Wizard-draft: tomt/ugyldig respons-objekt fra server');
    }
    if (typeof resp.draftId !== 'string' || resp.draftId.length === 0) {
      throw new Error('Wizard-draft: server-responsen mangler draftId — kontrakts-brudd');
    }
    if (typeof resp.publicId !== 'string' || resp.publicId.length === 0) {
      throw new Error('Wizard-draft: server-responsen mangler publicId — kontrakts-brudd');
    }
    // FE-only sync-metadata (syncStatus / lastSyncedAt / mode /
    // currentStep / currentStepSlug) ligger utenfor `resp` og bevares ved at
    // `state.value` spreader først. `createdAt`/`updatedAt` er wire-only og
    // utelates fra state.
    const { createdAt: _c, updatedAt: _u, mode: _m, ...beOwned } = resp;
    state.value = {
      ...state.value,
      ...beOwned,
      status: beOwned.status === undefined ? state.value.status : normalizeStatus(beOwned.status),
    } as WizardDraftState;
  }

  // ---- ensureDraft: lazy create på første artefakt-PUT ------------------

  let pendingCreate: Promise<string> | null = null;

  async function ensureDraft (): Promise<string> {
    if (state.value.publicId) return state.value.publicId;
    if (pendingCreate) return pendingCreate;
    if (!state.value.mode) {
      // FE-intern programmerings-feil: artifact-setter trigget før view-en
      // har initialisert draften via initDraftFromBackend. Vanligvis betyr
      // det at Nullstill/cancel resettet state.mode og view-en glemte å
      // re-initialisere før neste user-action. Kast konkret feil så
      // problemet ikke svelges (per [memory feedback_never_swallow_be_errors]).
      throw new Error('wizardDraftStore: artifact-setter kalt før initDraftFromBackend — view-en må re-initialisere draft etter Nullstill/cancel');
    }

    setSyncStatus('syncing');
    pendingCreate = (async (): Promise<string> => {
      const resp = await createDraft({ mode: state.value.mode as WizardMode });
      hydrateFromResponse(resp);
      const createdPublicId = state.value.publicId;
      if (!createdPublicId) {
        throw new Error('Server returnerte ingen publicId for ny draft');
      }
      notifyAfterCreate(createdPublicId);
      return createdPublicId;
    })();

    try {
      const id = await pendingCreate;
      setSyncStatus('idle', new Date().toISOString());
      return id;
    } catch (err) {
      const silent = (err as { silent?: boolean } | null)?.silent === true;
      if (!silent) {
        setSyncStatus('error');
        surfaceSyncError(err, 'Lagring av utkast feilet');
      }
      throw err;
    } finally {
      pendingCreate = null;
    }
  }

  // ---- Per-artefakt setters m/coalescing -------------------------------
  //
  // Hver setter:
  //  1. Oppdaterer lokal state optimistisk så UI rendrer umiddelbart.
  //  2. Sørger for at det finnes en publicId (kaller ensureDraft hvis nødvendig).
  //  3. Coalescer in-flight PUT på samme artefakt — neste kall venter på den
  //     forrige (sikrer last-save-wins per artefakt uten doble PUTs).

  type ArtifactKey = 'table' | 'fileConfig' | 'importDefinition' | 'columnMapping' | 'validation';
  const inflight: Record<ArtifactKey, Promise<void> | null> = {
    table: null,
    fileConfig: null,
    importDefinition: null,
    columnMapping: null,
    validation: null,
  };

  async function runArtifactPut (
    key: ArtifactKey,
    work: (id: string) => Promise<WizardDraftResponse>,
  ): Promise<void> {
    // Vent på forrige in-flight PUT for samme artefakt (last-save-wins).
    const previous = inflight[key];
    if (previous) {
      try { await previous; } catch { /* forrige feilet — fortsett uansett */ }
    }
    const task = (async (): Promise<void> => {
      const id = await ensureDraft();
      setSyncStatus('syncing');
      const resp = await work(id);
      hydrateFromResponse(resp);
      setSyncStatus('idle', resp.updatedAt ?? new Date().toISOString());
    })();
    inflight[key] = task;
    try {
      await task;
    } catch (err) {
      // Silent skip-feilen fra ensureDraft (race før mode er satt) skal IKKE
      // surface — det er en FE-intern timing-affordance, ikke en BE-feil.
      // Per [memory only-two-error-mechanisms-authorized].
      const silent = (err as { silent?: boolean } | null)?.silent === true;
      if (!silent) {
        setSyncStatus('error');
        surfaceSyncError(err, 'Lagring av utkast feilet');
      }
      throw err;
    } finally {
      // Clear bare hvis ingen nyere PUT har overskrevet feltet (best-effort).
      if (inflight[key] === task) {
        inflight[key] = null;
      }
    }
  }

  async function setTable (value: WizardDraftTable | null): Promise<void> {
    state.value.table = value;
    await runArtifactPut('table', (id) => putTable(id, value));
  }

  /**
   * WI 2088: synkron lokal oppdatering av tabell-utkastet UTEN draft-PUT.
   * Tabell-info-feltene (kontrollert <see TableInfoForm>) leser fra denne staten,
   * så de forblir reaktive per tastetrykk — mens selve persisteringen (setTable)
   * debounces av kalleren og flushes ved navigasjon. Ingen nettverksstøy per bokstav.
   */
  function setTableLocal (value: WizardDraftTable | null): void {
    state.value.table = value;
  }

  /**
   * WI #1232 runde 4 — R0: fil-switch-detection lever HER (i shell-storen),
   * ikke i legacy importWizardStore. Når brukeren bytter til en ny fil
   * (ny uploadId vs forrige), nullstiller vi mapping og valideringsregler
   * — tabell-info beholdes (tabell hører til mål-tabellen, ikke kildefilen).
   *
   * Smoketest 2026-05-21 (Image 11): brukeren beholdt feil mapping fra
   * forrige fil etter ny upload. Tidligere lå denne logikken i
   * importWizardStore.syncDraftFileConfigFromPreview, men det er en
   * legacy→shell cross-ref-brudd per [feedback_old_wiz_parallel]. Logikken
   * flyttes hit slik at shell er fullstendig isolert fra legacy-storen.
   */
  let lastSetUploadId: string | null = null;
  // WI #1662: i «Bruk eksisterende mal»-modus eies kolonne-mappingen av den
  // valgte malen, ikke av kildefilen. Da skal verken en filopplasting (inkl.
  // fler-arks child-uploads) eller en stale fil-switch-tracking tømme mappingen.
  // Settes av setColumnMapping når mappingen kommer fra en valgt mal.
  let mappingOwnedByTemplate = false;
  /**
   * Bug 3392: bytte-typen utledes av konfigen selv. `uploadId` er en `ContainerId` (WI 2318) — den
   * opplastede arbeidsboka — og arkvalget bæres av `selectedDataUploadId`. **En endret `uploadId`
   * ER derfor per definisjon en ny arbeidsbok**, og et arkbytte rører den ikke.
   *
   * Det gjør både modus-guarden og `newFile`-opsjonen fra Bug 3363 overflødige. 3363 lot kalleren si
   * fra fordi storen den gang ikke kunne se forskjell: en kontrakt-test simulerte arkbytte ved å
   * endre `uploadId`, og pinnet dermed en overlasting produksjonen aldri produserte. Da testens
   * premiss ble rettet, falt behovet for opplysningen bort.
   */
  async function setFileConfig (value: WizardFileConfigDraft | null): Promise<void> {
    const newUploadId = value?.uploadId ?? null;
    const previousUploadId = lastSetUploadId;
    const isFileSwitch = previousUploadId !== null
      && newUploadId !== null
      && previousUploadId !== newUploadId;

    state.value.fileConfig = value;
    if (newUploadId !== null) {
      lastSetUploadId = newUploadId;
    }
    await runArtifactPut('fileConfig', (id) => putUpload(id, value));

    // Bug 3392: bundel-unntaket som sto her er fjernet. Det fantes fordi et arkbytte i
    // CreateFromSetupFile trodde man endret uploadId — men arkvalget bæres av
    // selectedDataUploadId, så et arkbytte utløser ikke isFileSwitch i det hele tatt. Unntaket
    // vernet altså mot noe som ikke skjer, og prisen var at et EKTE filbytte i samme modus også
    // slapp unna (Bug 3363). Bundel-eid mapping overlever fortsatt et arkbytte — nå fordi
    // uploadId er uendret, ikke fordi modusen er unntatt.

    // Task 2583 (US 2524): et ekte fil-bytte gjør forrige prøvekjørings
    // duplikat-grunnlag ugyldig — nullstill valg + teller uansett hvem som
    // eier mappingen (mal-/bundel-unntakene under gjelder mapping, ikke
    // duplikat-beslutningen for kjøringen).
    if (isFileSwitch) {
      clearDuplicateState();
    }

    if (isFileSwitch && !mappingOwnedByTemplate) {
      // Nullstill kun mapping + validation. Tabell-state berøres ikke —
      // pre-fyllingen + brukerens manuelle endringer skal overleve fil-switch.
      // Mal-eid mapping (WI #1662) + bundel-eid mapping (CreateFromSetupFile)
      // hoppes over — de eies av hhv. malen og oppsettsfila, ikke kildefila.
      await setColumnMapping({ pairs: [] }).catch(() => undefined);
      await setValidationRules([]).catch(() => undefined);
    }
  }

  async function setImportDefinition (value: WizardImportDefinitionDraft | null): Promise<void> {
    state.value.importDefinition = value;
    await runArtifactPut('importDefinition', (id) => putImportDefinition(id, value));
  }

  /**
   * @param opts.ownedByTemplate — true når mappingen kommer fra en valgt
   *   eksisterende importmal («Bruk eksisterende mal»-modus). Da er mappingen
   *   mal-eid og skal ikke nullstilles av en etterfølgende filopplasting
   *   (WI #1662). Manuelt bygd mapping (auto-match / brukerredigering) settes
   *   uten flagget og nullstilles fortsatt ved ekte fil-bytte.
   */
  async function setColumnMapping (
    value: WizardColumnMappingDraft | null,
    opts?: { ownedByTemplate?: boolean },
  ): Promise<void> {
    state.value.columnMapping = value;
    mappingOwnedByTemplate = value !== null && opts?.ownedByTemplate === true;
    await runArtifactPut('columnMapping', (id) => putColumnMapping(id, value));
  }

  async function setValidationRules (value: WizardValidationRuleDraft[]): Promise<void> {
    const cloned = [...value];
    state.value.validationRules = cloned;
    await runArtifactPut('validation', (id) => putValidation(id, cloned));
  }

  /** Hent draft fra BE og hydrer lokal state. Brukes ved dyplenking. */
  async function restore (id: string): Promise<WizardDraftResponse> {
    setSyncStatus('syncing');
    // Bug #3013: run-tilstanden tilhører draften den ble satt for. Å laste en
    // ANNEN draft (ny publicId) er en ny kjøring — forrige kjørings fullført-
    // signaler/execute-resultat skal ikke lekke inn i den. Re-hydrering av
    // SAMME draft (retrySync / resultat-steg-resume, bug 1971) bevarer den.
    const previousPublicId = state.value.publicId;
    try {
      const resp = await fetchDraft(id);
      hydrateFromResponse(resp);
      if (state.value.publicId !== previousPublicId) {
        clearRunState();
      }
      // WI #1662: synk fil-switch-tracking til den gjenopprettede fila. Uten
      // dette står lastSetUploadId med en stale verdi fra forrige draft/økt, og
      // re-emit av samme fils config ved mount feilklassifiseres som et fil-bytte
      // → malens mapping tømmes. initDraft/initDraftFromBackend nullstiller den,
      // men restore() hydrerer fra BE uten å passere den veien.
      lastSetUploadId = state.value.fileConfig?.uploadId ?? null;
      setSyncStatus('idle', resp.updatedAt ?? new Date().toISOString());
      return resp;
    } catch (err) {
      setSyncStatus('error');
      surfaceSyncError(err, 'Kunne ikke gjenopprette utkast');
      throw err;
    }
  }

  /**
   * Avbryt draft — sletter på BE og resetter lokal state.
   *
   * Defensiv mot 404 (smoketest 2.0 #4/#5): "draft not found" på serveren er
   * IKKE en feil for klienten — målet er "draften skal være borte", og det
   * ER den. 404 behandles som suksess.
   *
   * 5xx/network-feil swallowes også (best-effort cleanup; TTL rydder uansett),
   * men logges for observability via console.warn så devs ser det i devtools.
   * Lokal state nullstilles uansett resultat.
   */
  async function cancel (): Promise<void> {
    const id = state.value.publicId;
    // WI 1965: etter terminal suksess er draften allerede ryddet av
    // markRunSucceeded(); ellers (også etter en FEILET kjøring) skal den slettes.
    if (id && !runSucceededRef.value) {
      try {
        await deleteDraft(id);
      } catch (err) {
        if (!isHttpNotFound(err)) {
          // 5xx / network — logges for diagnostikk; ikke kritisk.

          console.warn('Wizard-draft cancel(): non-404 server error, fortsetter med lokal cleanup', err);
        }
        // 404 = allerede borte fra server, ingenting å rydde der. Suksess.
      }
    }
    resetDraft();
  }

  /**
   * WI 1965: kalles av import-progress-steget når kjøringen når terminal SUKSESS.
   * Setter fullført-signalet og rydder draften på serveren — klient-eid cleanup
   * etter at execute sluttet å slette (BE-delen); Redis-TTL (24t) er sikkerhetsnett.
   * Best-effort: 404 = allerede ryddet; andre feil logges og overlates til TTL.
   */
  async function markRunSucceeded (): Promise<void> {
    if (runSucceededRef.value) {
      return;
    }
    runSucceededRef.value = true;
    const id = state.value.publicId;
    if (!id) {
      return;
    }
    try {
      await deleteDraft(id);
    } catch (err) {
      if (!isHttpNotFound(err)) {
        console.warn('Wizard-draft cleanup etter vellykket kjøring feilet — TTL rydder', err);
      }
    }
  }

  /**
   * Execute draft. BE krever `status === 'complete'`; FE har allerede gated UI.
   *
   * Smoketest 2.0 (#8): 4xx/5xx-feil fra execute (utenom strukturert
   * partial-result) bobler opp som global toast med BE-meldingen, slik at
   * brukeren ser hva som gikk galt. Ingen silent svelging
   * (memory `never_swallow_be_errors`).
   */
  async function execute (): Promise<WizardExecuteResult> {
    const id = state.value.publicId;
    if (!id) {
      throw new Error('Cannot execute draft without publicId — call a setter first to create draft');
    }
    setSyncStatus('syncing');
    try {
      // Task 2583 (US 2524): brukerens duplikat-valg for kjøringen sendes i
      // execute-body; uten valg beholdes den historiske body-løse signaturen.
      const resolution = duplicateResolutionRef.value;
      const result = await executeDraft(
        id,
        resolution === null ? undefined : { duplicateResolution: resolution },
      );
      // WI 1965: enqueue er ikke et utfall — draften består på serveren til
      // kjøringen faktisk lykkes (markRunSucceeded) eller brukeren avbryter.
      startedRunRef.value = true;
      lastExecuteResultRef.value = result;
      setSyncStatus('idle', new Date().toISOString());
      return result;
    } catch (err) {
      setSyncStatus('error');
      // Felles error-rendering: BE-meldingen vises som global danger-toast.
      // View'en trenger ikke duplisere — den får fortsatt throwen og kan
      // gjøre lokal UX (eks. tilbake-til-dry-run-knapp), men brukeren ser
      // alltid feilmeldingen.
      surfaceSyncError(err, 'Wizard-execute feilet');
      throw err;
    }
  }

  /**
   * US 1848 task 2058: blank save-or-run-bannerets feil. Kalles av steget når en
   * ny handling starter («Lagre …»-knappene) så et tidligere feilbanner ryddes.
   */
  function clearSaveOrRunError (): void {
    saveOrRunErrorRef.value = null;
  }

  /**
   * US 1848 task 2058: sett save-or-run-bannerets feiltekst. Brukes for
   * navigasjonsfeil ved «Lagre mal og avslutt» (router-push som rejecter) —
   * feilen eies av storen, ikke en view-lokal ref.
   */
  function reportSaveOrRunError (message: string): void {
    saveOrRunErrorRef.value = message;
  }

  /**
   * US 1848 task 2058: kjør execute for save-or-run-steget uten å boble en throw
   * til komponenten. `execute()` beholder sin throw-kontrakt (andre views +
   * etablerte tester avhenger av den + den globale danger-toasten); denne
   * wrapper-en eier i tillegg save-or-run-bannerets feiltekst og returnerer et
   * diskriminert resultat slik at komponenten slipper egen try/catch.
   */
  async function executeForSaveOrRun (): Promise<WizardExecuteResult | null> {
    saveOrRunErrorRef.value = null;
    try {
      return await execute();
    } catch (err) {
      saveOrRunErrorRef.value = err instanceof Error ? err.message : 'Kunne ikke kjøre importen.';
      return null;
    }
  }

  /**
   * Plukk serverens faktiske feilmelding fra et axios-lignende feilobjekt.
   * Leser ASP.NET ProblemDetails i prioritert rekkefølge: `detail` (mest
   * spesifikk custom-melding) → `errors` (validerings-felt, flatet) → `title`
   * (ofte generisk) → `message`. Returnerer null hvis ingen finnes.
   */
  function extractBackendMessage (err: unknown): string | null {
    const axiosLike = err as { response?: { data?: unknown } } | null;
    const data = axiosLike?.response?.data;
    if (!data || typeof data !== 'object') return null;
    const d = data as { detail?: unknown; title?: unknown; errors?: unknown; message?: unknown };
    if (typeof d.detail === 'string' && d.detail.trim().length > 0) return d.detail.trim();
    const flattened = flattenProblemErrors(d.errors);
    if (flattened) return flattened;
    if (typeof d.title === 'string' && d.title.trim().length > 0) return d.title.trim();
    if (typeof d.message === 'string' && d.message.trim().length > 0) return d.message.trim();
    return null;
  }

  /** Flat ProblemDetails `errors`-mappen (felt → meldinger[]) til én lesbar streng. */
  function flattenProblemErrors (errors: unknown): string | null {
    if (!errors || typeof errors !== 'object') return null;
    const parts: string[] = [];
    for (const value of Object.values(errors as Record<string, unknown>)) {
      if (Array.isArray(value)) {
        for (const m of value) if (typeof m === 'string' && m.trim().length > 0) parts.push(m.trim());
      } else if (typeof value === 'string' && value.trim().length > 0) {
        parts.push(value.trim());
      }
    }
    return parts.length > 0 ? parts.join(' ') : null;
  }

  /**
   * Overflat en draft-synkfeil til brukeren via det etablerte varsel-systemet
   * (notificationsStore) med serverens faktiske årsak. Guardet mot dobbel-varsel
   * når samme feil bobler gjennom flere catch-lag (f.eks. create-feil fanget av
   * både ensureDraft og runArtifactPut).
   */
  function surfaceSyncError (err: unknown, title: string): void {
    const tagged = err as { __surfaced?: boolean } | null;
    if (tagged && typeof tagged === 'object' && tagged.__surfaced) return;
    const message = extractBackendMessage(err) ?? describeSyncError(err);
    useNotificationsStore().addError(title, message);
    if (tagged && typeof tagged === 'object') tagged.__surfaced = true;
  }

  /**
   * Kjør auto-match: analyser fila via legacy `filePreviewStore`, mapper
   * resultatene til `WizardColumnMappingPair`-er og lagrer mappingen via
   * per-artefakt-PUT. Eier sin egen busy + feil-håndtering så .vue-shells
   * kan kalle den uten try/catch.
   */
  async function autoMatch (opts: AutoMatchOptions): Promise<void> {
    if (autoMatchBusy.value) return;
    const notificationsStore = useNotificationsStore();
    const tableKey = opts.tableKey.trim();
    if (!tableKey) {
      notificationsStore.addError('Auto-match', 'Mangler tabell — velg tabell i steg 1 først');
      return;
    }
    autoMatchBusy.value = true;
    try {
      const filePreviewStore = useFilePreviewStore();
      const ok = await filePreviewStore.analyseFile(tableKey, opts.importDefinitionKey);
      if (!ok) {
        notificationsStore.addError('Auto-match', 'Analyse av filen feilet');
        return;
      }
      const comparison = filePreviewStore.analyse?.columnsComparison;
      if (!comparison) {
        notificationsStore.addError('Auto-match', 'Analysen returnerte ingen resultater');
        return;
      }
      const entries = Object.values(comparison) as AnalyseCol[];
      const { next, statusCounts, appliedCount } = buildAutoMatchMapping(
        entries,
        state.value.columnMapping?.pairs ?? [],
      );
      // Bare PUT når vi faktisk har endret noe. Empty/uendret PUT gir bare
      // unødvendige round-trips og kan trigge falske «Synk feilet»-toaster
      // hvis BE er busy/restarting (axios «Network Error» uten konkret response).
      if (appliedCount > 0) {
        await setColumnMapping({ pairs: next });
        notificationsStore.addSuccess(
          'Auto-match',
          `${appliedCount} kolonner mappet automatisk`,
        );
      } else {
        const summary = Object.entries(statusCounts)
          .map(([s, n]) => `${s}: ${n}`)
          .join(', ');
        const summarySuffix = summary ? ` — ${summary}` : '';
        notificationsStore.addWarn(
          'Auto-match',
          `Ingen automatiske treff funnet (${entries.length} kolonner analysert${summarySuffix})`,
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ukjent feil ved auto-match';
      notificationsStore.addError('Auto-match feilet', message);
    } finally {
      autoMatchBusy.value = false;
    }
  }

  /** Re-prøv siste mislykkede PUT. Med per-artefakt PUTs er vi mer presise enn
   *  før — vi sender ikke alt på nytt; vi prøver bare å re-bekrefte status
   *  ved å hente draften. Det avdekker stale-error som er resolvet allerede. */
  async function retrySync (): Promise<void> {
    const id = state.value.publicId;
    if (!id) {
      setSyncStatus('idle');
      return;
    }
    await restore(id);
  }

  return {
    // Exposed state slice (read-only via computed; mutasjoner kun via actions).
    draftId,
    publicId,
    mode,
    hasStartedRun,
    hasCompletedSuccessfully,
    lastExecuteResult,
    currentStep,
    currentStepSlug,
    table,
    fileConfig,
    importDefinition,
    columnMapping,
    validationRules,
    resolvedDatasetId,
    containerId,
    establishedTableRules,
    saveOrRunError,
    duplicateResolution,
    dryRunDuplicates,
    status,
    nextStep,
    fileColumns,
    stepGates,
    blockReasonForStep,
    setupFileAnalysis,
    validationRuleWarnings,
    isComplete,
    syncStatus,
    lastSyncedAt,
    autoMatchBusy,
    // Actions.
    initDraft,
    initDraftFromBackend,
    setTable,
    setTableLocal,
    setFileConfig,
    setImportDefinition,
    setColumnMapping,
    setValidationRules,
    setEstablishedTableRules,
    refreshEstablishedTableRules,
    flipEstablishedRuleSeverity,
    setDuplicateResolution,
    setDryRunDuplicates,
    setStep,
    setSyncStatus,
    resetDraft,
    onAfterFirstSave,
    // Server-koblede actions.
    restore,
    cancel,
    execute,
    executeForSaveOrRun,
    clearSaveOrRunError,
    reportSaveOrRunError,
    markRunSucceeded,
    autoMatch,
    retrySync,
  };
});
